import React, { useState, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent } from '@material-ui/core';
import styles from './PopupManager.module.css';
import { MdClose, MdThumbUp, MdErrorOutline } from 'react-icons/md';
import { FaInfo } from 'react-icons/fa';
import clsx from 'clsx';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import { MESSAGE_TYPE, DISPLAY_MESSAGES } from '../../constants';
import { getErrorMessage } from '../../utils/utils';
import GridLoadingSpinner from '../../components/GridLoadingSpinner/GridLoadingSpinner';
export const PopupContext = createContext(null);

export default ({ children }) => {
  const [popupType, setPopupType] = useState(MESSAGE_TYPE.INFORMATION);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [messageData, setMessageData] = useState({
    contextText: null, // the first line/s at the top of the dialog content
    info: null, // the rest of the line/s at the bottom of the dialog content
  });

  const openPopup = () => setIsPopupOpen(true);
  const closePopup = () => setIsPopupOpen(false);

  const setInternalErrorMessageContext = () => {
    setMessageData({
      contextText: 'Please refresh the page.',
      info: 'If issue persists, please contact to the local IT team.',
    });
    setPopupType(MESSAGE_TYPE.INTERNAL_ERROR);
  };

  const setNetworkErrorMessageContext = () => {
    setMessageData({
      contextText:
        'Please check your network connection or try refreshing the browser.',
      info: 'If issue persists, please contact your local network team.',
    });
    setPopupType(MESSAGE_TYPE.NETWORK_ERROR);
  };

  const setContextForErrorObjectScenario = (
    type,
    error,
    contextText = DISPLAY_MESSAGES.CLEAR_ERRORS,
  ) => {
    if (error.response && error.response.status === 500) {
      setInternalErrorMessageContext();
    } else {
      const errors = getErrorMessage(error);
      setMessageData({ contextText, info: errors });
      setPopupType(type || MESSAGE_TYPE.FAILURE);
    }
  };

  const showPopup = ({ contextText, info, type, error }) => {
    if (type === MESSAGE_TYPE.INTERNAL_ERROR) {
      setInternalErrorMessageContext();
    } else if (error) {
      const isNetworkError =
        !error.response && error.message === 'Network Error';
      const isTimeoutError =
        error.response?.status === 408 || error.code === 'ECONNABORTED';
      const isServerError = error.response?.status === 500;
      if (isTimeoutError || isNetworkError) {
        setNetworkErrorMessageContext();
      } else if (isServerError) {
        setInternalErrorMessageContext();
      } else {
        setContextForErrorObjectScenario(
          type || MESSAGE_TYPE.FAILURE,
          error,
          contextText,
        );
      }
    } else {
      setMessageData({ contextText, info });
      setPopupType(type || MESSAGE_TYPE.INFORMATION);
    }
    openPopup();
  };

  const showSuccess = args => {
    showPopup({ ...args, type: MESSAGE_TYPE.SUCCESS });
    console.log('showSuccess Called', args);
  };

  const showError = args => showPopup({ ...args, type: MESSAGE_TYPE.FAILURE });

  const showInternalError = () =>
    showPopup({ type: MESSAGE_TYPE.INTERNAL_ERROR });

  const showInformation = args =>
    showPopup({ ...args, type: MESSAGE_TYPE.INFORMATION });

  const showLoading = args => {
    showPopup({ type: MESSAGE_TYPE.LOADING });
    return closePopup;
  };

  const closePopUp = args => {
    return closePopup();
  };

  return (
    <>
      <Popup
        open={isPopupOpen}
        handleClose={closePopup}
        messageData={messageData}
        type={popupType}
      />
      <PopupContext.Provider
        value={{
          showPopup,
          showError,
          showSuccess,
          showInternalError,
          showInformation,
          showLoading,
          closePopUp,
        }}
      >
        {children}
      </PopupContext.Provider>
    </>
  );
};

export const usePopupManager = () => useContext(PopupContext);

const Popup = ({ open, type, handleClose, messageData }) => {
  let dialogIcon = null;
  let dialogTitleModifierClass = null;
  let title = null;

  switch (type) {
    case MESSAGE_TYPE.SUCCESS:
      dialogIcon = <MdThumbUp className={styles.icfail} />;
      dialogTitleModifierClass = styles.success;
      title = 'Success';
      break;
    case MESSAGE_TYPE.FAILURE:
      dialogIcon = <NotInterestedIcon className={styles.icfail} />;
      dialogTitleModifierClass = styles.failure;
      title = 'Fail';
      break;
    case MESSAGE_TYPE.INFORMATION:
      dialogIcon = <FaInfo className={styles.icfail} />;
      dialogTitleModifierClass = styles.information;
      title = 'Information';
      break;
    case MESSAGE_TYPE.INTERNAL_ERROR:
      dialogIcon = <MdErrorOutline className={styles.icfail} />;
      dialogTitleModifierClass = styles.internalError;
      title = 'Looks like something has gone wrong..';
      break;
    case MESSAGE_TYPE.NETWORK_ERROR:
      dialogIcon = <MdErrorOutline className={styles.icfail} />;
      dialogTitleModifierClass = styles.internalError;
      title = 'Network issue';
      break;
    default:
      break;
  }

  const renderMessage = () => {
    if (
      !messageData.contextText ||
      (Array.isArray(messageData.contextText) &&
        messageData.contextText.length === 0)
    )
      return null;

    const renderTexts = textLines =>
      textLines.map((errorMessage, index) => (
        <>
          <div className={clsx(styles.error, styles.errorMessage)} key={index}>
            - {errorMessage}
          </div>
          <br />
        </>
      ));

    const renderText = text => (
      <span className={clsx(styles.error, styles.errorMessage)}>{text}</span>
    );

    const renderInfo = () => {
      const { info } = messageData;
      if (Array.isArray(info)) {
        return renderTexts(info);
      } else {
        return renderText(info);
      }
    };

    return (
      <div className={styles.errorsContainer}>
        <p className={styles.editMessage}>{messageData.contextText}</p>
        <br />
        <p className={styles.errorList}>{renderInfo()}</p>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      className={styles.dialogContainer}
      classes={{
        paper:
          type === MESSAGE_TYPE.LOADING ? styles.spinnerBox : styles.dialogBox,
      }}
      data-testid="popup-message"
    >
      {type === MESSAGE_TYPE.LOADING ? (
        <GridLoadingSpinner />
      ) : (
        <>
          <DialogTitle>
            <span className={clsx(styles.title, dialogTitleModifierClass)}>
              {dialogIcon}
              <span className={styles.txt}>{title}</span>
            </span>
            <MdClose
              className={styles.cancelIcon}
              onClick={handleClose}
              data-testid="close-icon"
            />{' '}
          </DialogTitle>
          <DialogContent className={styles.content}>
            {renderMessage()}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};

Popup.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  response: PropTypes.shape({
    title: PropTypes.string,
    data: PropTypes.object,
    status: PropTypes.number,
  }),
};
